'use client';

import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { DebtPageHeader } from '@/components/layout/DebtPageHeader';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useAuth } from '@/contexts/customer-auth-context';
import { useTranslation } from '@/hooks/useTranslation';
import { Loader } from '@ophelos/ui/components/loader';
import { PaymentHistoryList } from './_partials/_clearedDebtPartials/PaymentHistoryList';
import { DebtBreakdownTab } from './_partials/_shared/DebtBreakdownTab';
import { filterCustomerTransactions } from './_partials/_shared/TransactionsList';

export default function DebtCleared() {
  const { debtId } = useParams<{ debtId: string }>();
  const navigate = useNavigate();
  const { isPreviewMode, isLoading: configLoading } = useConfiguration();
  const { t } = useTranslation();
  const { accountId } = useAuth();
  const { debts, isLoading } = useDebtData();

  // Find debt from cached data
  const debt = useMemo(() => {
    if (!debtId) return null;
    return debts.find((d) => d.id === debtId) || null;
  }, [debts, debtId]);

  // Redirect if not authenticated or no debtId
  useEffect(() => {
    if (!accountId && !isPreviewMode) {
      navigate('/login');
      return;
    }

    if (!debtId) {
      navigate(isPreviewMode ? '/dashboard?preview=true' : '/dashboard');
      return;
    }
  }, [accountId, debtId, navigate, isPreviewMode]);

  // Must stay below the hooks above — returning ahead of them changes hook order between renders.
  if (configLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader fullScreen />
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout showFooter={false}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader fullScreen />
        </div>
      </Layout>
    );
  }

  if (!debt) {
    return (
      <Layout showFooter={false}>
        <Breadcrumb />
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-destructive">{t('payment.debtNotFound')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* <Breadcrumb /> */}

      <div className="mx-auto flex-1 h-full w-full space-y-2">
        <DebtPageHeader backTo={isPreviewMode ? '/dashboard?preview=true' : '/dashboard'} creditor={debt} />
        <DebtBreakdownTab debt={debt} history={<PaymentHistoryList account={debt} transactions={filterCustomerTransactions(debt.transactions)} />} />
      </div>
    </Layout>
  );
}
